// Shared helpers for the Training / Certification module.

// API base looks like "http://localhost:3000/api/v1"; uploaded videos are served
// from the host root ("/uploads/..."). Strip the "/api/v1" suffix to get the origin.
const apiOrigin = (() => {
    const base = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api/v1';
    return base.replace(/\/api\/v1\/?$/, '');
})();

/** Resolve a video URL for playback. Absolute URLs pass through; "/uploads/..." is
 *  resolved against the API origin. Returns null for locked videos. */
export const resolveVideoUrl = (videoUrl: string | null): string | null => {
    if (!videoUrl) return null;
    if (/^https?:\/\//i.test(videoUrl)) return videoUrl;
    return `${apiOrigin}${videoUrl.startsWith('/') ? '' : '/'}${videoUrl}`;
};

const YT_RE = /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{11})/;
const VIMEO_RE = /vimeo\.com\/(?:video\/)?(\d+)/;

/** Returns an embeddable iframe src for YouTube/Vimeo links, or null if the URL
 *  should be played with a native <video> element instead. */
export const getEmbedUrl = (videoUrl: string): string | null => {
    const yt = videoUrl.match(YT_RE);
    if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
    const vimeo = videoUrl.match(VIMEO_RE);
    if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;
    return null;
};

/** Pull a human-readable message out of an RTK Query / fetch error. Handles the
 *  backend's string-or-array `message` shape. */
export const readableError = (err: unknown, fallback = 'Something went wrong'): string => {
    const data = (err as { data?: { message?: unknown } })?.data;
    const message = data?.message ?? (err as { message?: unknown })?.message;
    if (Array.isArray(message)) return message.join(', ');
    if (typeof message === 'string') return message;
    return fallback;
};

/** Format a duration in seconds as "m:ss" (0 / missing → empty string). */
export const formatDuration = (seconds: number): string => {
    if (!seconds || seconds <= 0) return '';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
};
